import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../api/axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useAuth } from '../contexts/authcontext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const STEPS = { SLOT: 0, DETAILS: 1, UPLOAD: 2, DONE: 3 };

const PHONE_REGEX = /^\+?[0-9]{10,15}$/;

const GraphBookingPage = () => {
  const { graphId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [step, setStep] = useState(STEPS.SLOT);
  const [graphologist, setGraphologist] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [phoneError, setPhoneError] = useState('');

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { referrer: `/booking/graphologist/${graphId}` } });
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [graphRes, slotsRes] = await Promise.all([
          axios.get(`/graphologists/${graphId}`),
          axios.get(`/graph/public-slots/${graphId}`)
        ]);
        setGraphologist(graphRes.data);
        setSlots(slotsRes.data.available || []);
      } catch (err) {
        toast.error('Could not load graphologist details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [graphId, isAuthenticated, navigate]);

  // Revoke the object URL when the preview changes or the page unmounts,
  // otherwise each re-selected photo leaks a blob for the tab's lifetime.
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    if (user?.name && !name) setName(user.name);
    if (user?.email && !email) setEmail(user.email);
    if (user?.phone && !phone) setPhone(user.phone);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (selected.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }

    if (preview) URL.revokeObjectURL(preview);

    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  };

  const handleDetailsContinue = () => {
    const trimmedPhone = phone.trim().replace(/[\s-]/g, '');

    // Phone is what session reminders are sent to, so validate it rather than
    // letting an unusable number through as "optional".
    if (!PHONE_REGEX.test(trimmedPhone)) {
      setPhoneError('Enter a valid phone number (10-15 digits)');
      return;
    }

    setPhoneError('');
    setStep(STEPS.UPLOAD);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Please upload a photo of your handwriting');
      return;
    }
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('slotId', selectedSlot.id);
      formData.append('graphologistId', graphId);
      formData.append('clientName', name.trim());
      formData.append('clientEmail', email.trim());
      formData.append('clientPhone', phone.trim().replace(/[\s-]/g, ''));
      formData.append('handwritingSample', file);

      // Content-Type is set automatically by the browser, including the
      // multipart boundary. Setting it by hand omits the boundary and the
      // server cannot parse the body.
      const { data } = await axios.post('/graph-bookings', formData);

      if (data.success) {
        setResult(data);
        setStep(STEPS.DONE);
        toast.success('Booking confirmed');
      } else {
        toast.error(data.error || 'Booking failed');
      }
    } catch (err) {
      const status = err.response?.status;

      if (status === 409) {
        // Someone booked this slot first. Send them back to pick another.
        toast.error(err.response?.data?.error || 'That slot was just taken. Please choose another time.');
        setStep(STEPS.SLOT);
        setSelectedSlot(null);
        try {
          const slotsRes = await axios.get(`/graph/public-slots/${graphId}`);
          setSlots(slotsRes.data.available || []);
        } catch { /* leave the stale list rather than blanking the page */ }
      } else {
        toast.error(err.response?.data?.error || 'Booking failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  if (!graphologist) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px' }}>
        <p>Graphologist not found.</p>
        <Link to="/doctors?tab=graphologists">Back to the directory</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '40px auto', padding: '0 24px' }}>
      <div style={{
        background: 'white',
        borderRadius: 24,
        padding: 40,
        boxShadow: '0 12px 32px rgba(48,60,108,0.08)',
        border: '1px solid rgba(48,60,108,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
          <img
            src={graphologist.image ? `${BACKEND_URL}/uploads/${graphologist.image}` : '/img/6971412.png'}
            alt={graphologist.name}
            onError={(e) => { e.target.src = '/img/6971412.png'; }}
            style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }}
          />
          <div>
            <h3 style={{ fontFamily: "'DM Serif Display', serif", margin: 0, color: '#1a2344' }}>
              {graphologist.name}
            </h3>
            <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>{graphologist.specialisation}</p>
          </div>
        </div>

        {/* ── Step 1: Pick a slot ── */}
        {step === STEPS.SLOT && (
          <>
            <h4 style={{ marginBottom: 16 }}>Choose a time</h4>
            {slots.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No available slots right now — please check back later.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
                {slots.map(slot => (
                  <button
                    key={slot.id}
                    onClick={() => setSelectedSlot(slot)}
                    style={{
                      padding: '14px 12px',
                      borderRadius: 12,
                      border: selectedSlot?.id === slot.id ? '2px solid #e8622a' : '1.5px solid rgba(48,60,108,0.15)',
                      background: selectedSlot?.id === slot.id ? 'rgba(232,98,42,0.06)' : 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: 13
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{moment(slot.start_time).format('MMM D')}</div>
                    <div style={{ color: '#6b7280' }}>{moment(slot.start_time).format('h:mm A')}</div>
                  </button>
                ))}
              </div>
            )}
            <button
              disabled={!selectedSlot}
              onClick={() => setStep(STEPS.DETAILS)}
              style={{ ...primaryBtn, opacity: selectedSlot ? 1 : 0.5 }}
            >
              Continue
            </button>
          </>
        )}

        {/* ── Step 2: Contact details ── */}
        {step === STEPS.DETAILS && (
          <>
            <h4 style={{ marginBottom: 16 }}>Your details</h4>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Full name</label>
              <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} required />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} required />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={e => { setPhone(e.target.value); setPhoneError(''); }}
                style={{ ...inputStyle, borderColor: phoneError ? '#e74c3c' : 'rgba(48,60,108,0.15)' }}
              />
              <span style={{ fontSize: 12, color: phoneError ? '#e74c3c' : '#6b7280', marginTop: 4, display: 'block' }}>
                {phoneError || 'We will send your appointment reminder here'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(STEPS.SLOT)} style={secondaryBtn}>Back</button>
              <button
                disabled={!name || !email}
                onClick={handleDetailsContinue}
                style={{ ...primaryBtn, opacity: (name && email) ? 1 : 0.5 }}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Upload handwriting sample ── */}
        {step === STEPS.UPLOAD && (
          <>
            <h4 style={{ marginBottom: 8 }}>Upload your handwriting</h4>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
              Good lighting, flat surface, no shadows — a few lines of your normal handwriting is enough.
              This is required so {graphologist.name} can prepare before your session.
            </p>
            <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>
              Your sample is private. Only you and {graphologist.name} can see it.
            </p>

            <label
              htmlFor="handwriting-upload"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: 32,
                borderRadius: 16,
                border: '2px dashed rgba(48,60,108,0.2)',
                cursor: 'pointer',
                marginBottom: 24,
                background: '#fafbfc'
              }}
            >
              {preview ? (
                <img src={preview} alt="Your handwriting sample" style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 12 }} />
              ) : (
                <>
                  <span style={{ fontSize: 32 }} aria-hidden="true">📷</span>
                  <span style={{ color: '#374151', fontWeight: 600, fontSize: 14 }}>Tap to take or choose a photo</span>
                </>
              )}
              <input
                id="handwriting-upload"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </label>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(STEPS.DETAILS)} style={secondaryBtn} disabled={submitting}>Back</button>
              <button
                disabled={!file || submitting}
                onClick={handleSubmit}
                style={{ ...primaryBtn, opacity: (!file || submitting) ? 0.5 : 1 }}
              >
                {submitting ? 'Booking…' : 'Confirm Booking'}
              </button>
            </div>
          </>
        )}

        {/* ── Step 4: Confirmation ── */}
        {step === STEPS.DONE && result && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">✅</div>
            <h4 style={{ marginBottom: 8 }}>Booking confirmed</h4>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
              Your session with {result.graphologistName} is set for{' '}
              {moment(result.appointmentTime).format('MMMM D, h:mm A')}.
              Your handwriting sample has been received.
            </p>
            {/* Previously navigated to /my-bookings, which is not a registered
                route — a successful booking landed the client on a 404. There
                is no bookings page in the app yet, so this returns home. */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => navigate('/doctors?tab=graphologists')} style={secondaryBtn}>
                Book another
              </button>
              <button onClick={() => navigate('/home')} style={primaryBtn}>
                Back to home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1.5px solid rgba(48,60,108,0.15)',
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none'
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' };

const primaryBtn = {
  flex: 1,
  padding: '14px 24px',
  borderRadius: 50,
  border: 'none',
  background: '#1a2344',
  color: 'white',
  fontWeight: 600,
  fontSize: 15,
  cursor: 'pointer'
};

const secondaryBtn = {
  flex: 1,
  padding: '14px 24px',
  borderRadius: 50,
  border: '1.5px solid rgba(48,60,108,0.15)',
  background: 'white',
  color: '#374151',
  fontWeight: 600,
  fontSize: 15,
  cursor: 'pointer'
};

export default GraphBookingPage;